import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Select,
  FormControl,
  FormLabel,
  Text,
  Spinner,
  Box,
  Input,
  Textarea,
  FormErrorMessage,
} from "@chakra-ui/react";
import { blockManualDateInput, blockDatePaste } from "common/utils/dateInputUtils";
import emailTemplateService from "services/Recruit/services/emailTemplate";

const InviteEmailModal = ({ isOpen, onClose, onSend, isLoading }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewTime, setInterviewTime] = useState("");
  const [interviewAddress, setInterviewAddress] = useState("");
  const [googleMapLink, setGoogleMapLink] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const isValidHttpUrl = (value) => {
    if (!value) {
      return true;
    }
    try {
      const parsed = new URL(value);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch (error) {
      return false;
    }
  };

  const trimmedGoogleMapLink = googleMapLink.trim();
  const isGoogleMapLinkValid = isValidHttpUrl(trimmedGoogleMapLink);

  const minDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setSelectedTemplate("");
      setInterviewDate("");
      setInterviewTime("");
      setInterviewAddress("");
      setGoogleMapLink("");
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await emailTemplateService.getTemplates({
        allData: true,
        isActive: true,
      });
      const allTemplates = response?.data || [];
      // Only show templates with application category
      setTemplates(allTemplates.filter(t => t.category === 'application'));
    } catch (error) {
      console.error("Failed to fetch email templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSendClick = () => {
    if (
      selectedTemplate &&
      interviewDate &&
      interviewTime &&
      interviewAddress.trim() &&
      isGoogleMapLinkValid
    ) {
      onSend({
        templateId: selectedTemplate,
        interviewDate,
        interviewTime,
        interviewAddress: interviewAddress.trim(),
        googleMapLink: trimmedGoogleMapLink,
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Invite Email</ModalHeader>
        <ModalCloseButton isDisabled={isLoading} />
        <ModalBody>
          {loadingTemplates ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Spinner />
            </Box>
          ) : templates.length === 0 ? (
            <Text color="gray.500">
              No email templates available. Please create an email template first.
            </Text>
          ) : (
            <>
              <FormControl mb={4}>
                <FormLabel>
                  Email Template <span className="astrick">*</span>
                </FormLabel>
                <Select
                  placeholder="Select an email template"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  isDisabled={isLoading}
                >
                  {templates.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.name} ({template.category || 'custom'})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>
                  Interview Date <span className="astrick">*</span>
                </FormLabel>
                <Input
                  type="date"
                  min={minDate}
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  isDisabled={isLoading}
                  onKeyDown={blockManualDateInput}
                  onPaste={blockDatePaste}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>
                  Interview Time <span className="astrick">*</span>
                </FormLabel>
                <Input
                  type="time"
                  value={interviewTime}
                  onChange={(e) => setInterviewTime(e.target.value)}
                  isDisabled={isLoading}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  Interview Address <span className="astrick">*</span>
                </FormLabel>
                <Textarea
                  placeholder="Enter interview address"
                  value={interviewAddress}
                  onChange={(e) => setInterviewAddress(e.target.value)}
                  isDisabled={isLoading}
                />
              </FormControl>

              <FormControl mt={4} isInvalid={Boolean(trimmedGoogleMapLink) && !isGoogleMapLinkValid}>
                <FormLabel>Google Map Link (Optional)</FormLabel>
                <Input
                  type="url"
                  placeholder="https://maps.google.com/..."
                  value={googleMapLink}
                  onChange={(e) => setGoogleMapLink(e.target.value)}
                  isDisabled={isLoading}
                />
                <FormErrorMessage>
                  Enter a valid URL starting with http:// or https://
                </FormErrorMessage>
              </FormControl>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSendClick}
            isLoading={isLoading}
            isDisabled={
              !selectedTemplate ||
              !interviewDate ||
              !interviewTime ||
              !interviewAddress.trim() ||
              (Boolean(trimmedGoogleMapLink) && !isGoogleMapLinkValid) ||
              isLoading ||
              loadingTemplates
            }
          >
            Send Email
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InviteEmailModal;
